import { useEffect, useRef, useState } from "react";
import { Banner, LoadingLine } from "../ui/common";
import { AccessUnauthorizedError, clear_access_password, resolve_initial_password } from "../lib/access_api";
import { load_secrets } from "../lib/secrets_boot";

/**
 * Startup password gate. The app's secrets (OpenAI key, AWS credentials) are not bundled — they
 * are fetched from the Access API behind a single shared password the user enters once per tab
 * session. Until that succeeds, nothing else renders.
 *
 * If a password is already stored from this session, it is tried automatically (a reload doesn't
 * re-prompt). A wrong password is forgotten and the form is shown.
 */
export function PasswordGate({ onReady }: { onReady: () => void }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Auto-try a stored password exactly once on mount (guarded against StrictMode double-invoke).
  const autoTried = useRef(false);

  async function attempt(pw: string) {
    setBusy(true);
    setError(null);
    try {
      await load_secrets(pw);
      onReady();
    } catch (e: any) {
      if (e instanceof AccessUnauthorizedError) {
        clear_access_password();
        setError("Wrong password — try again.");
      } else {
        setError(e?.message ? String(e.message) : String(e));
      }
      setBusy(false);
    }
  }

  useEffect(() => {
    if (autoTried.current) return;
    autoTried.current = true;
    // A password in the URL hash (#…) auto-unlocks, taking precedence over a session-stored one.
    const initial = resolve_initial_password();
    if (initial) void attempt(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!busy && password.trim()) void attempt(password.trim());
  }

  if (busy) {
    return (
      <main className="app-main">
        <LoadingLine message="Unlocking the app…" />
      </main>
    );
  }

  return (
    <main className="app-main">
      <header className="lagna-header">
        <div className="lagna-brand">
          <div className="logo-mark" aria-hidden>
            <div className="ring" />
            <div className="star" />
          </div>
          <div>
            <div className="eyebrow">Birth-Time Rectification</div>
            <div className="display-title">The Lagna Navigator</div>
          </div>
        </div>
      </header>

      <div className="card" style={{ maxWidth: 420, margin: "10vh auto 0", padding: 24 }}>
        <h2 style={{ marginTop: 0, fontSize: 18 }}>Enter the access password</h2>
        <p style={{ color: "var(--ink2)", fontSize: 13, lineHeight: 1.5 }}>
          This app fetches its keys from a secure store. Enter the shared password to unlock cloud
          storage and AI features for this session.
        </p>
        {error && <Banner kind="error">{error}</Banner>}
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
          <input
            type="password"
            autoFocus
            autoComplete="current-password"
            aria-label="Access password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{ padding: "10px 12px", fontSize: 14 }}
          />
          <button type="submit" disabled={!password.trim()} className="mono">
            Unlock
          </button>
        </form>
      </div>
    </main>
  );
}
